import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useEffect, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useNavigate } from 'react-router-dom';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import Combobox from '@/components/combo-box';
import {
    useGetBoardsQuery,
    useGetUsersQuery,
    useCreateCourseMutation,
} from '@/state/course';
import type { ApiErrorResponse } from '@/types';
import { getMessageByStatus } from '@/lib/error-message';
import { useTenant } from '@/hooks';
import { Sparkles } from 'lucide-react';

const courseFormSchema = z.object({
    course_name: z.string().min(1, 'Course name is required'),
    course_code: z.string().min(1, 'Course code is required'),
    full_marks: z.string().min(1, 'Full marks is required').refine(
        (val) => {
            const num = parseFloat(val);
            return !isNaN(num) && num >= 0;
        },
        { message: 'Full marks must be a number greater than or equal to 0' }
    ),
    board: z.string().min(1, 'Board is required'),
    lecturer: z.string().min(1, 'Lecturer is required'),
});

type FormValues = z.infer<typeof courseFormSchema>;

interface CreateCourseFormProps {
    isOpen: boolean;
    onClose: () => void;
    canGenerateCourse?: boolean;
    defaultBoardId?: number;
}

const CreateCourseForm = ({
    isOpen,
    onClose,
    canGenerateCourse = false,
    defaultBoardId,
}: CreateCourseFormProps) => {
    const { tenant } = useTenant();
    const navigate = useNavigate();
    const form = useForm<FormValues>({
        resolver: zodResolver(courseFormSchema),
        defaultValues: {
            course_name: '',
            course_code: '',
            full_marks: '',
            board: defaultBoardId ? String(defaultBoardId) : '',
            lecturer: '',
        },
    });

    useEffect(() => {
        if (isOpen && defaultBoardId) {
            form.setValue('board', String(defaultBoardId), { shouldDirty: false });
        }
    }, [defaultBoardId, form, isOpen]);

    const { data: boards, isLoading: isBoardsLoading } = useGetBoardsQuery(
        tenant ? { tenant } : skipToken
    );
    const { data: users, isLoading: isUsersLoading } = useGetUsersQuery(
        tenant ? { tenant } : skipToken
    );
    const [createCourse, { isLoading: isCreating }] = useCreateCourseMutation();

    const { formState } = form;
    const { isDirty } = formState;

    const boardOptions = useMemo(() => {
        if (!boards) return [];
        return boards.map((board) => ({
            label: board.board_name,
            value: board.id.toString(),
        }));
    }, [boards]);

    const lecturerOptions = useMemo(() => {
        if (!users) return [];
        return users.map((user) => ({
            label: `${user.profile.first_name} ${user.profile.last_name}`,
            value: user.id,
        }));
    }, [users]);

    const onSubmit = async (data: FormValues) => {
        if (!tenant) {
            toast.error('No tenant found', {
                description: 'Please select an organization first.',
            });
            return;
        }

        try {
            await createCourse({
                tenant,
                data: {
                    course_name: data.course_name,
                    course_code: data.course_code,
                    full_marks: data.full_marks,
                    board: parseInt(data.board),
                    lecturer: data.lecturer,
                },
            }).unwrap();

            toast.success('Course created', {
                description: 'Your course has been created successfully',
            });

            form.reset();
            onClose();
        } catch (err) {
            const error = err as ApiErrorResponse;
            toast.error('Error creating course', {
                description: getMessageByStatus(error?.status || 500),
            });
        }
    };

    const handleClose = () => {
        if (!isCreating) {
            form.reset();
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[625px]">
                <DialogHeader className="mb-4">
                    <DialogTitle>Create Course</DialogTitle>
                    <DialogDescription>
                        Add a new course to your organization
                    </DialogDescription>
                </DialogHeader>
                {canGenerateCourse ? (
                    <div className="mb-4 rounded-xl border-0 bg-primary/5 p-3 shadow-sm">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm font-medium">Want AI to help build this course?</p>
                                <p className="text-xs text-muted-foreground">
                                    Use the generator if you want to create materials from a curriculum file.
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                className="shrink-0"
                                onClick={() => {
                                    if (!tenant) return;
                                    form.reset();
                                    onClose();
                                    navigate(`/${tenant}/courses/generate`);
                                }}
                            >
                                <Sparkles className="mr-2 h-4 w-4" />
                                Generate Course
                            </Button>
                        </div>
                    </div>
                ) : null}
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-6 overflow-y-auto max-h-[calc(100vh-250px)]"
                    >
                        <FormField
                            control={form.control}
                            name="course_name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Course Name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Enter course name" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="board"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Board</FormLabel>
                                    <FormControl>
                                        <Combobox
                                            options={boardOptions}
                                            selectedOption={field.value}
                                            onSelect={field.onChange}
                                            placeholder={
                                                isBoardsLoading ? 'Loading boards...' : 'Select board'
                                            }
                                            searchable
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="lecturer"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Lecturer</FormLabel>
                                    <FormControl>
                                        <Combobox
                                            options={lecturerOptions}
                                            selectedOption={field.value}
                                            onSelect={field.onChange}
                                            placeholder={
                                                isUsersLoading
                                                    ? 'Loading lecturers...'
                                                    : 'Select lecturer'
                                            }
                                            searchable
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="flex gap-4 w-full">
                            <FormField
                                control={form.control}
                                name="course_code"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormLabel>Course Code</FormLabel>
                                        <FormControl>
                                            <Input placeholder="e.g., CS101" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="full_marks"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormLabel>Full Marks</FormLabel>
                                        <FormControl>
                                            <Input
                                                type="number"
                                                placeholder="e.g., 100"
                                                min="0"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="flex justify-end gap-6">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleClose}
                                disabled={isCreating}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isCreating || !isDirty}>
                                {isCreating ? 'Creating...' : 'Create Course'}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default CreateCourseForm;
